import React, { FC, useMemo } from "react";

import { usePagedRows } from "../../../components/pagination/usePagedRows";
import { WorkModel } from "../../../factories/works/models/work.model";
import { useGetWorkSequencesQuery } from "../../../slices/EmailSequencesSlice";
import { exportDataToExcel } from "../../../utils/ExportData";
import { EmailSequence } from "../../work/pages/sequences/emailSequence.types";
import { askTable, filtersText } from "../ask/reportAsk.utils";
import {
  daysInStatus,
  formatDate,
  peopleOf,
  statusGroup,
} from "../reports.utils";
import { scopeUserIds, useReportsAccess } from "../reportsAccess";
import { PeopleClientTypeFilters, useReportFilters } from "../ui/ReportFilters";
import {
  AssigneeCell,
  Card,
  Empty,
  Kpi,
  KpiGrid,
  LoadError,
  Loading,
  NUM,
  ReportPage,
  StatusPill,
  TableBox,
  TD,
  TH,
  useFocusRows,
  WorkLink,
} from "../ui/ReportUi";
import { useAllWorks } from "../useReportData";

/** The assignee as the table shows it (see AssigneeCell), for "Ask about this report". */
const assigneeText = (w: WorkModel) => {
  const people = peopleOf(w);
  if (!people[0]?.id) {
    return w.roleName ? `Role: ${w.roleName}` : "Not assigned";
  }
  return people.map((p) => p.name).join(", ");
};

/** "Sequence · 2 of 4 sent" for the work's running sequence, if any. */
const sequenceText = (sequences?: EmailSequence[]) => {
  const active = sequences?.find((s) => s.status === "Active");
  if (!active) return null;
  const sent = active.steps.filter((s) => s.sentAt).length;
  return `Sequence · ${sent} of ${active.steps.length} sent`;
};

const ReminderCell: FC<{ workId: string }> = ({ workId }) => {
  const { data, isLoading } = useGetWorkSequencesQuery({ workId });
  if (isLoading) return <span className={"text-[#5B6472]"}>…</span>;
  const text = sequenceText(data);
  return text ? (
    <span
      className={
        "inline-block rounded-full bg-[#F0EDFF] px-2.5 py-[3px] text-xs font-bold text-[#4B32D6]"
      }>
      {text}
    </span>
  ) : (
    <span
      className={
        "inline-block rounded-full bg-[#F0F1F5] px-2.5 py-[3px] text-xs font-bold text-[#3F4757]"
      }>
      No sequence
    </span>
  );
};

export const WaitingReport: FC = () => {
  const access = useReportsAccess();
  const state = useReportFilters();
  const { filters } = state;
  const works = useAllWorks({
    hideGreenStatus: true,
    workStatuses: ["Waiting"],
    userIds: scopeUserIds(access, filters.userIds),
    customerIds: filters.clientIds,
    worktypeIds: filters.workTypeIds,
  });
  const rows = useMemo(
    () =>
      works.data
        .filter((w) => statusGroup(w) === "Waiting")
        .map((work) => ({ work, days: daysInStatus(work) }))
        .sort((a, b) => (b.days ?? -1) - (a.days ?? -1)) as {
        work: WorkModel;
        days: number | null;
      }[],
    [works.data],
  );
  const known = rows.filter((r) => r.days !== null) as {
    work: WorkModel;
    days: number;
  }[];
  const over7 = known.filter((r) => r.days > 7).length;
  const avg = known.length
    ? known.reduce((s, r) => s + r.days, 0) / known.length
    : 0;
  const v = (x: React.ReactNode) => (works.isLoading ? "…" : x);

  const onExport = () =>
    exportDataToExcel(
      rows.map(({ work, days }) => ({
        Work: work.workName,
        Client: work.customerName,
        Status: work.jobSubStatus || work.jobStatus,
        "Waiting since": formatDate(work.statusUpdatedAt),
        Days: days ?? "",
        Assignee: peopleOf(work)
          .map((p) => p.name)
          .join(", "),
        "Due date": formatDate(work.dueDate),
      })),
      "Waiting on client",
    );

  const tableName = "Work waiting on the client";
  const shownRows = useFocusRows(tableName, rows, (r) => r.work.workId);
  const { pageRows, pager } = usePagedRows(shownRows);

  const ask = useMemo(
    () => ({
      title: "Waiting on client",
      path: "/reports/waiting",
      description:
        "Work in a Waiting status: it can't move until the client replies or sends something. Days are counted since the status changed.",
      filters: filtersText([!access.firmWide && "Only your own work"]),
      kpis: [
        { label: "Waiting on client", value: String(rows.length) },
        { label: "Waiting over 7 days", value: String(over7) },
        { label: "Average wait", value: `${Math.round(avg * 10) / 10} days` },
      ],
      tables: [
        askTable(
          tableName,
          rows,
          [
            {
              key: "work",
              label: "Work",
              kind: "text",
              value: (r) => r.work.workName,
            },
            {
              key: "client",
              label: "Client",
              kind: "text",
              value: (r) => r.work.customerName,
            },
            {
              key: "status",
              label: "Status",
              kind: "text",
              value: (r) => r.work.jobSubStatus || r.work.jobStatus,
            },
            {
              key: "since",
              label: "Waiting since",
              kind: "date",
              value: (r) => formatDate(r.work.statusUpdatedAt),
            },
            {
              key: "days",
              label: "Days",
              kind: "number",
              value: (r) => r.days,
            },
            {
              key: "assignee",
              label: "Assignee",
              kind: "text",
              value: (r) => assigneeText(r.work),
            },
            {
              key: "due",
              label: "Due date",
              kind: "date",
              value: (r) => formatDate(r.work.dueDate),
            },
          ],
          (r) => r.work.workId,
          (r) => `/work/${r.work.workId}`,
        ),
      ],
    }),
    [rows, over7, avg, access.firmWide],
  );

  return (
    <ReportPage
      title={"Waiting on client"}
      description={
        "Work in a Waiting status: it can't move until the client replies or sends something. The longest wait first."
      }
      onExport={rows.length ? onExport : undefined}
      ask={works.isLoading ? undefined : ask}
      askSuggestions={[
        "Which clients have kept us waiting the longest?",
        "What should we follow up on first?",
        "Is any waiting work already past its due date?",
      ]}
      filters={
        <PeopleClientTypeFilters state={state} showPeople={access.firmWide} />
      }>
      {works.isError && <LoadError />}
      <KpiGrid>
        <Kpi
          label={"Waiting on client"}
          value={v(rows.length)}
          note={"Work items"}
        />
        <Kpi
          label={"Waiting over 7 days"}
          value={v(over7)}
          note={"May need a follow-up"}
          tone={"red"}
        />
        <Kpi
          label={"Average wait"}
          value={v(`${Math.round(avg * 10) / 10} days`)}
          note={"Since the status changed"}
        />
      </KpiGrid>

      <Card title={"Work waiting on the client"}>
        {works.isLoading ? (
          <Loading />
        ) : works.isError ? null : !rows.length ? (
          <Empty text={"No work is waiting on a client."} />
        ) : (
          <TableBox pager={pager}>
            <thead>
              <tr>
                <th scope={"col"} className={TH}>
                  Work
                </th>
                <th scope={"col"} className={TH}>
                  Client
                </th>
                <th scope={"col"} className={TH}>
                  Status
                </th>
                <th scope={"col"} className={TH}>
                  Waiting since
                </th>
                <th scope={"col"} className={`${TH} ${NUM}`}>
                  Days
                </th>
                <th scope={"col"} className={TH}>
                  Reminders
                </th>
                <th scope={"col"} className={TH}>
                  Assignee
                </th>
                <th scope={"col"} className={TH}>
                  Due date
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(({ work, days }) => (
                <tr key={work.workId}>
                  <td className={TD}>
                    <WorkLink id={work.workId} name={work.workName} />
                  </td>
                  <td className={TD}>{work.customerName}</td>
                  <td className={TD}>
                    <StatusPill
                      group={"Waiting"}
                      label={work.jobSubStatus || work.jobStatus}
                    />
                  </td>
                  <td className={TD}>{formatDate(work.statusUpdatedAt)}</td>
                  <td
                    className={`${TD} ${NUM} font-extrabold ${
                      days !== null && days >= 14
                        ? "text-[#B42318]"
                        : days !== null && days > 7
                        ? "text-[#8A4100]"
                        : ""
                    }`}>
                    {days ?? "—"}
                  </td>
                  <td className={TD}>
                    <ReminderCell workId={work.workId} />
                  </td>
                  <td className={TD}>
                    <AssigneeCell
                      people={peopleOf(work)}
                      role={work.roleName}
                    />
                  </td>
                  <td className={TD}>{formatDate(work.dueDate)}</td>
                </tr>
              ))}
            </tbody>
          </TableBox>
        )}
      </Card>
    </ReportPage>
  );
};
